import React, { useMemo } from 'react';

import { MarginRisk } from '@swyftx/margin/build/types';

import { Big } from '@shared/safe-big';
import { cn } from '@shared/utils/lib/ui';

import { useMarginAccount } from 'src/lib/margin/hooks/account';

type Props = {
  className?: string;
  stepIncrement?: number;
  maxIncrement?: number;
  customMarginLevel?: Big;
  customRiskRating?: MarginRisk;
};

const MarginAccountHealthBarElement: React.FC<Props> = ({
  className,
  stepIncrement = 10,
  maxIncrement = 250,
  customMarginLevel,
  customRiskRating,
}) => {
  const { marginLevel: accountMarginLevel, riskRating: accountRiskRating } = useMarginAccount();

  const marginLevel = useMemo(() => customMarginLevel || accountMarginLevel, [accountMarginLevel, customMarginLevel]);

  const riskRating = useMemo(() => customRiskRating || accountRiskRating, [accountRiskRating, customRiskRating]);

  const marginPercentage = useMemo(() => {
    if (!marginLevel) return Big(0);

    if (Big(marginLevel).gt(maxIncrement)) return Big(100);

    return Big(marginLevel)
      .div(maxIncrement + stepIncrement)
      .times(100);
  }, [marginLevel, maxIncrement, stepIncrement]);

  const barColor = useMemo(() => {
    switch (riskRating) {
      case MarginRisk.Monitor:
        return 'bg-color-background-info';
      case MarginRisk.Warning:
        return 'bg-color-background-warning';
      case MarginRisk.AtRisk:
        return 'bg-color-background-error';
      case MarginRisk.StopOut:
        return 'bg-color-background-error';
      case MarginRisk.None:
        return 'bg-color-background-neutral';
      case MarginRisk.Healthy:
        return '';
      default:
        riskRating satisfies never;
    }
  }, [riskRating]);

  return <div className={cn(className, barColor)} style={{ width: `${marginPercentage}%` }} />;
};

export { MarginAccountHealthBarElement };
